/**
 * Integrations View — Connect and manage non-messaging integrations (Cloud Storage, Dev Tools, Calendar).
 *
 * Two states:
 *   1. Integration list (default) — shows all integrations grouped by category
 *   2. Detail view — shows info and disconnect button for a connected integration
 *
 * OAuth flow (production):
 *   1. User clicks "Connect" → signInWithOAuth redirects to Google
 *   2. Google redirects back to /#integrations with provider_token in session
 *   3. connectedCallback detects pending connect, sends tokens to edge function
 *
 * Dev mode: skips redirect, calls mock service directly.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  getServices,
  type IntegrationInfo,
  type IntegrationType,
} from "../services/index.ts";
import {
  CONNECTABLE_INTEGRATIONS,
  INTEGRATION_CATEGORY_LABELS,
  type IntegrationMeta,
} from "../components/platform-icons.ts";

type ViewState =
  | { kind: "list" }
  | { kind: "detail"; integration: IntegrationInfo };

@customElement("katanoid-integrations-view")
export class KatanoidIntegrationsView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private integrations: IntegrationInfo[] = [];
  @state() private loading = true;
  @state() private view: ViewState = { kind: "list" };
  @state() private connecting: string | null = null; // integration type currently connecting
  @state() private connectError: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.initAndLoad();
  }

  /**
   * Load integrations, then check if we're returning from an OAuth redirect.
   * If a pending connect is found, grab the provider_token from the Supabase
   * session and finalize the connection server-side.
   */
  private async initAndLoad(): Promise<void> {
    await this.loadIntegrations();
    await this.checkPendingConnect();
  }

  private async loadIntegrations(): Promise<void> {
    this.loading = true;
    try {
      const { integrations } = getServices();
      this.integrations = await integrations.listIntegrations();
    } catch {
      this.integrations = [];
    }
    this.loading = false;
  }

  /**
   * After an OAuth redirect, check for success/error indicators.
   *
   * New flow (server-side code exchange):
   *   - The edge function handles the Google callback and stores tokens directly.
   *   - It redirects back to /#integrations?connect_success=gdrive or ?connect_error=...
   *   - We just need to detect these URL params and refresh the list.
   *
   * Legacy flow (implicit/provider_token):
   *   - sessionStorage has a pending type, and we wait for provider_token.
   *   - Kept as fallback for any existing sessions mid-connect.
   */
  private async checkPendingConnect(): Promise<void> {
    // ── New flow: check URL params from server-side callback ──
    const hash = window.location.hash; // e.g. "#integrations?connect_success=gdrive"
    const qIdx = hash.indexOf("?");
    if (qIdx !== -1) {
      const params = new URLSearchParams(hash.substring(qIdx + 1));
      const connectSuccess = params.get("connect_success");
      const connectError = params.get("connect_error");

      // Clean up the URL params
      window.location.hash = "integrations";

      // Also clear any stale pending connect flag
      sessionStorage.removeItem("katanoid_pending_integration");

      if (connectSuccess) {
        // Server already stored tokens — just refresh the list
        await this.loadIntegrations();
        return;
      }

      if (connectError) {
        this.connectError = connectError;
        return;
      }
    }

    // ── Legacy flow cleanup ──
    // Clear any stale pending connect flag from sessionStorage.
    // The legacy implicit-flow path (which used Supabase Auth provider tokens)
    // has been removed because those tokens only carry basic profile scopes,
    // not the Calendar/Drive scopes needed for integrations. All connects now
    // use the server-side code exchange flow above.
    const { getPendingIntegrationConnect } = await import(
      "../services/real-integrations.ts"
    );
    const pendingType = getPendingIntegrationConnect();
    if (pendingType) {
      // If we reach here, the server-side callback redirect didn't include
      // connect_success — something went wrong during the OAuth flow.
      this.connectError =
        "Integration connect did not complete. The OAuth callback may have failed — please try again.";
    }
  }

  // ── Main render ──

  protected override render(): TemplateResult {
    if (this.view.kind === "detail")
      return this.renderDetail(this.view.integration);
    return this.renderList();
  }

  // ── List view ──

  private renderList(): TemplateResult {
    // Group CONNECTABLE_INTEGRATIONS by category
    const categories = new Map<string, IntegrationMeta[]>();
    for (const meta of CONNECTABLE_INTEGRATIONS) {
      const list = categories.get(meta.category) ?? [];
      list.push(meta);
      categories.set(meta.category, list);
    }

    // Build a set of connected integration types for quick lookup
    const connectedTypes = new Set(this.integrations.map((i) => i.type));

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Integrations</h1>
        </div>

        ${this.loading
          ? html`
              <div class="channel-wizard-list">
                <div class="shimmer shimmer--card"></div>
                <div class="shimmer shimmer--card"></div>
                <div class="shimmer shimmer--card"></div>
              </div>
            `
          : html`
              ${this.connectError
                ? html`
                    <div
                      class="integration-error"
                      style="
                      background: var(--danger-bg, rgba(255,59,48,0.1));
                      color: var(--danger, #ff3b30);
                      padding: 12px 16px;
                      border-radius: 10px;
                      font-size: 14px;
                      margin-bottom: 16px;
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                    "
                    >
                      <span>${this.connectError}</span>
                      <button
                        class="btn btn--ghost btn--sm"
                        style="color: var(--danger, #ff3b30);"
                        @click=${() => {
                          this.connectError = null;
                        }}
                      >
                        Dismiss
                      </button>
                    </div>
                  `
                : nothing}
              ${Array.from(categories.entries()).map(([category, metas]) =>
                this.renderCategory(category, metas, connectedTypes),
              )}
            `}
      </div>
    `;
  }

  private renderCategory(
    category: string,
    metas: IntegrationMeta[],
    connectedTypes: Set<string>,
  ): TemplateResult {
    const label = INTEGRATION_CATEGORY_LABELS[category] ?? category;

    return html`
      <div style="margin-bottom: 28px;">
        <h3
          style="
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 12px 0;
        "
        >
          ${label}
        </h3>

        <div class="channel-wizard-list">
          ${metas.map((meta) => {
            // Google Workspace services (Sheets, Docs, Slides, Forms) share the gdrive connection
            const GDRIVE_ALIASES = ["gsheets", "gdocs", "gslides", "gforms"];
            const effectiveType = GDRIVE_ALIASES.includes(meta.id) ? "gdrive" : meta.id;
            const isConnected = connectedTypes.has(effectiveType);
            const connectedInfo = this.integrations.find(
              (i) => i.type === effectiveType,
            );
            const isConnecting = this.connecting === meta.id;

            if (isConnected && connectedInfo) {
              return this.renderConnectedCard(meta, connectedInfo);
            }

            if (meta.available) {
              return this.renderAvailableCard(meta, isConnecting);
            }

            return this.renderComingSoonCard(meta);
          })}
        </div>
      </div>
    `;
  }

  private renderConnectedCard(
    meta: IntegrationMeta,
    info: IntegrationInfo,
  ): TemplateResult {
    return html`
      <div
        class="channel-card"
        @click=${() => {
          this.view = { kind: "detail", integration: info };
        }}
        style="cursor: pointer;"
      >
        <div
          class="channel-card-icon channel-card-icon--tinted"
          style="color: ${meta.color};"
        >
          <span .innerHTML=${meta.svg}></span>
        </div>
        <div class="channel-card-info">
          <div class="channel-card-name">${meta.name}</div>
          <div class="channel-card-status">
            <span class="status-dot status-dot--connected"></span>
            Connected${info.connectedEmail
              ? html` &middot; ${info.connectedEmail}`
              : nothing}
          </div>
        </div>
        <span class="channel-card-chevron">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </span>
      </div>
    `;
  }

  private renderAvailableCard(
    meta: IntegrationMeta,
    isConnecting: boolean,
  ): TemplateResult {
    return html`
      <div class="channel-card" style="cursor: default;">
        <div
          class="channel-card-icon channel-card-icon--tinted"
          style="color: ${meta.color};"
        >
          <span .innerHTML=${meta.svg}></span>
        </div>
        <div class="channel-card-info">
          <div class="channel-card-name">${meta.name}</div>
          <div
            class="channel-card-desc"
            style="color: var(--text-tertiary); font-size: 13px;"
          >
            Ready to connect
          </div>
        </div>
        <button
          class="btn btn--primary btn--sm"
          ?disabled=${isConnecting}
          @click=${(e: Event) => {
            e.stopPropagation();
            this.startConnect(meta.id as IntegrationType);
          }}
        >
          ${isConnecting
            ? html`<span class="btn-spinner"></span> Connecting...`
            : "Connect"}
        </button>
      </div>
    `;
  }

  private renderComingSoonCard(meta: IntegrationMeta): TemplateResult {
    return html`
      <div
        class="channel-card"
        style="opacity: 0.45; pointer-events: none;"
      >
        <div
          class="channel-card-icon channel-card-icon--tinted"
          style="color: ${meta.color};"
        >
          <span .innerHTML=${meta.svg}></span>
        </div>
        <div class="channel-card-info">
          <div class="channel-card-name">${meta.name}</div>
          <div
            class="channel-card-desc"
            style="color: var(--text-tertiary); font-size: 13px;"
          >
            Coming soon
          </div>
        </div>
        <span
          style="
          font-size: 11px;
          font-weight: 600;
          color: var(--text-tertiary);
          background: var(--bg-tertiary);
          padding: 4px 10px;
          border-radius: 100px;
          white-space: nowrap;
        "
          >Soon</span
        >
      </div>
    `;
  }

  // ── Detail view ──

  private renderDetail(integration: IntegrationInfo): TemplateResult {
    const meta = CONNECTABLE_INTEGRATIONS.find(
      (i) => i.id === integration.type,
    );
    const connectedDate = integration.connectedAt
      ? new Date(integration.connectedAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Unknown";

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button
            class="btn btn--ghost btn--sm"
            @click=${() => this.backToList()}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            Back
          </button>
          <h1 class="view-title">
            ${meta?.name ?? integration.displayName}
          </h1>
        </div>

        <div class="channel-detail-card" style="margin-bottom: 24px;">
          ${meta
            ? html`
                <div
                  style="display: flex; align-items: center; gap: 14px; margin-bottom: 20px;"
                >
                  <div
                    class="channel-card-icon channel-card-icon--tinted"
                    style="color: ${meta.color}; width: 48px; height: 48px;"
                  >
                    <span .innerHTML=${meta.svg}></span>
                  </div>
                  <div>
                    <div
                      style="font-size: 18px; font-weight: 600; color: var(--text-primary);"
                    >
                      ${meta.name}
                    </div>
                    <div
                      style="font-size: 13px; color: var(--text-secondary);"
                    >
                      <span
                        class="status-dot status-dot--connected"
                      ></span>
                      Connected
                    </div>
                  </div>
                </div>
              `
            : nothing}

          <div style="display: grid; gap: 12px; font-size: 14px;">
            ${integration.connectedEmail
              ? html`
                  <div
                    style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-subtle);"
                  >
                    <span style="color: var(--text-secondary);"
                      >Account</span
                    >
                    <span style="color: var(--text-primary);"
                      >${integration.connectedEmail}</span
                    >
                  </div>
                `
              : nothing}
            <div
              style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-subtle);"
            >
              <span style="color: var(--text-secondary);">Connected</span>
              <span style="color: var(--text-primary);"
                >${connectedDate}</span
              >
            </div>
            <div
              style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-subtle);"
            >
              <span style="color: var(--text-secondary);">Access</span>
              <span style="color: var(--text-primary);"
                >Read &amp; Write</span
              >
            </div>
            <div
              style="display: flex; justify-content: space-between; padding: 8px 0;"
            >
              <span style="color: var(--text-secondary);">Status</span>
              <span
                style="color: var(--success, #30d158); font-weight: 500;"
                >Active</span
              >
            </div>
          </div>
        </div>

        <div style="display: flex; gap: 12px;">
          <button
            class="btn btn--danger btn--sm"
            @click=${() => this.handleDisconnect(integration)}
            style="
              background: var(--danger-bg, rgba(255,59,48,0.1));
              color: var(--danger, #ff3b30);
              border: none;
              padding: 10px 20px;
              border-radius: 10px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
            "
          >
            Disconnect
          </button>
        </div>
      </div>
    `;
  }

  // ── Connect flow ──

  private async startConnect(type: IntegrationType): Promise<void> {
    this.connecting = type;
    this.connectError = null;

    const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

    if (isDevMode) {
      // Dev mode: no redirect needed — call startConnect + completeConnect directly
      try {
        const { integrations } = getServices();
        await integrations.startConnect(type);
        const result = await integrations.completeConnect(
          type,
          "mock-provider-token",
          null,
        );
        if (result.success) {
          await this.loadIntegrations();
        } else {
          this.connectError = result.error ?? "Connection failed";
        }
      } catch (err) {
        this.connectError =
          err instanceof Error ? err.message : "Connection failed";
      }
      this.connecting = null;
      return;
    }

    // Production: startConnect triggers signInWithOAuth redirect.
    // The page navigates away; on return, connectedCallback → checkPendingConnect
    // will pick up the provider_token and finalize.
    try {
      const { integrations } = getServices();
      await integrations.startConnect(type);
      // If we reach here, the redirect didn't happen (shouldn't normally occur)
    } catch (err) {
      this.connectError =
        err instanceof Error ? err.message : "Failed to start OAuth";
      this.connecting = null;
    }
  }

  private async handleDisconnect(integration: IntegrationInfo): Promise<void> {
    try {
      const { integrations } = getServices();
      await integrations.disconnect(integration.id);
      await this.loadIntegrations();
      this.view = { kind: "list" };
    } catch (err) {
      console.error("Failed to disconnect:", err);
    }
  }

  private backToList(): void {
    this.view = { kind: "list" };
    this.loadIntegrations(); // Refresh list
  }
}
